// components/TopBar.js
import React, { useState, useEffect, useContext } from 'react';
import { Slide } from '@mui/material';
import ChainedLM from '../assets/ChainedLM.png';
import SlidingSlidebar from './SlidingSlidebar';
import { AppContextisChecked } from '../context/AppContextisChecked';
import Navbar from './Navbar';
const TopBar = ({ setIsChecked: setParentIsChecked }) => {
  const { isChecked: contextIsChecked, setIsChecked: setContextIsChecked } = useContext(AppContextisChecked);
  const [localIsChecked, setLocalIsChecked] = useState(() => {
    try {
      const storedValue = localStorage.getItem('isChecked');
      return storedValue !== null ? JSON.parse(storedValue) : false;
    } catch (e) {
      // console.error('Error parsing isChecked from localStorage:', e);
      return false;
    }
  });
  
  // State to control tooltip visibility
  const [operatorTooltipVisible, setOperatorTooltipVisible] = useState(false);
  const [researchTooltipVisible, setResearchTooltipVisible] = useState(false);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'isChecked') {
        try {
          const newValue = event.newValue !== null ? JSON.parse(event.newValue) : false;
          setLocalIsChecked(newValue);
          setParentIsChecked(newValue);
          setContextIsChecked(newValue);
          // console.log('TopBar: isChecked updated from storage event:', newValue);
        } catch (error) {
          // console.error('TopBar: Error parsing isChecked from storage event:', error);
          setLocalIsChecked(false);
          setParentIsChecked(false);
          setContextIsChecked(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [setParentIsChecked, setContextIsChecked]);

  useEffect(() => {
    if (localIsChecked !== contextIsChecked) {
      try {
        localStorage.setItem('isChecked', JSON.stringify(contextIsChecked));
        setLocalIsChecked(contextIsChecked);
        setParentIsChecked(contextIsChecked);
        // console.log('TopBar: localIsChecked synced with contextIsChecked:', contextIsChecked);
      } catch (error) {
        // console.error("Error setting isChecked in localStorage:", error);
        // Optionally, provide user feedback if localStorage is critical
        // alert("Failed to save settings. Please check your browser's storage settings.");
      }
    }
  }, [contextIsChecked, setParentIsChecked]);

  const containerStyle = { 
    position: 'relative', 
    display: 'inline-flex', 
    alignItems: 'center', 
    borderRadius: '20px', 
    overflow: 'hidden', 
    border: '1px solid #333', 
    borderColor: 'rgba(145, 148, 153, 0.84)', 
    backgroundColor: '#1f1f1f', 
    fontFamily: 'sans-serif', 
    textAlign: 'center', 
    marginLeft: 'auto' 
  };
  
  const baseButtonStyle = { 
    padding: '6px 16px', 
    cursor: 'pointer', 
    userSelect: 'none', 
    transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
    position: 'relative' 
  };
  
  const planStyle = { 
    ...baseButtonStyle, 
    backgroundColor: localIsChecked ? '#f0c600' : 'transparent', 
    color: localIsChecked ? 'white' : 'white' 
  };
  
  const actStyle = { 
    ...baseButtonStyle, 
    backgroundColor: localIsChecked ? 'transparent' : '#2196f3', 
    color: localIsChecked ? 'white' : 'white' 
  };

  // Tooltip styles - positioned outside the container
  const tooltipStyle = {
    position: 'absolute',
    top: '100%', // Position below the button
    left: '50%',
    transform: 'translateX(-50%)',
    marginTop: '10px', // Gap between button and tooltip
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    color: 'white',
    padding: '6px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    zIndex: 100, // High z-index to ensure visibility
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
    transition: 'opacity 0.2s, visibility 0.2s',
    opacity: 0,
    visibility: 'hidden',
    pointerEvents: 'none'
  };

  // Active tooltip style
  const activeTooltipStyle = {
    ...tooltipStyle,
    opacity: 1,
    visibility: 'visible'
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '4rem', background: 'linear-gradient(45deg, rgb(255 255 255), rgb(113 112 112 / 81%))', borderBottom: '1px solid rgb(229, 231, 235)', display: 'flex', alignItems: 'center', paddingRight: '1.5rem', zIndex: 5 }}>
      <h1 style={{ fontSize: '1.5rem', fontFamily: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif', color: 'rgb(17 24 39)', letterSpacing: '-0.025em' }}>
        {/* ChainedLLM */}
      </h1>
      <SlidingSlidebar />
      <img style={{ height: '64px', marginLeft: '0px', borderRadius: '0%' }} src={ChainedLM} alt="ChainedLLM" />
      
      {/* Container with the toggle buttons */}
      <div style={containerStyle}>
        {/* Operator button */}
        <div 
          style={{...planStyle, position: 'relative'}} 
          onClick={() => { 
            localStorage.setItem('isChecked', JSON.stringify(true)); 
            setLocalIsChecked(true); 
            setParentIsChecked(true); 
            setContextIsChecked(true); 
          }}
          onMouseEnter={() => setOperatorTooltipVisible(true)}
          onMouseLeave={() => setOperatorTooltipVisible(false)}
        >
          Operator
        </div>
        
        {/* Research button */}
        <div 
          style={{...actStyle, position: 'relative'}}
          onClick={() => { 
            localStorage.setItem('isChecked', JSON.stringify(false)); 
            setLocalIsChecked(false); 
            setParentIsChecked(false); 
            setContextIsChecked(false); 
          }}
          onMouseEnter={() => setResearchTooltipVisible(true)}
          onMouseLeave={() => setResearchTooltipVisible(false)}
        >
          Research
        </div>
      </div>
      
      {/* Tooltips positioned outside container to avoid overflow:hidden issues */}
      {operatorTooltipVisible && (
        <div style={{
          position: 'absolute',
          top: '4rem', // Position below the top bar
          right: "-10px", 
          transform: 'translateX(-30%)',
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          color: 'white',
          padding: '6px 10px',
          borderRadius: '4px',
          fontSize: '15px',
          whiteSpace: 'nowrap',
          zIndex: 100,
          boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
        }}>
          Switch to AI Agent Operator mode
        </div>
      )}
      
      {researchTooltipVisible && (
        <div style={{
          position: 'absolute',
          top: '4rem', // Position below the top bar
          right: '10px', // Align with the Research button
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          color: 'white',
          padding: '6px 10px',
          borderRadius: '4px',
          fontSize: '15px',
          whiteSpace: 'nowrap',
          zIndex: 100,
          boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
        }}>
          Switch to research and analysis mode
        </div>
      )}


      <Navbar/>
    </div>
  );
};

export default TopBar;
